import {useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  getBanksList,
  getBankBranchesList,
  handleCurrentStep,
  paymentMethodValidation,
  saveToHistory,
  selectBankObligationInfo,
  selectBanksList,
  selectSelectedBanData,
  setBankObligationInfo,
  setSelectedBansForPaymentUpdate,
  setShowPopup,
} from '../DebtAndUpdatePaymentBlockSlice';

const getListFromResponse = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload?.Body)) {
    return payload.Body;
  }
  return [];
};

export const useBankBranchSelection = () => {
  const dispatch = useDispatch();
  const selectedBanData = useSelector(selectSelectedBanData);
  const banksList = useSelector(selectBanksList);
  const bankObligationInfo = useSelector(selectBankObligationInfo);
  const selectedBansForPaymentUpdate = useSelector((state) => state.debtAndUpdatePayment.selectedBansForPaymentUpdate);
  const [branchesList, setBranchesList] = useState([]);

  useEffect(() => {
    if (!selectedBansForPaymentUpdate?.length && selectedBanData?.banNumber) {
      dispatch(setSelectedBansForPaymentUpdate([selectedBanData.banNumber]));
    }
  }, [dispatch, selectedBanData?.banNumber, selectedBansForPaymentUpdate?.length]);

  const bankOptions = useMemo(
    () =>
      banksList
        .filter((bank) => (bank.bankAgreement ?? '').trim() !== '')
        .map((bank) => ({
          key: bank.bankCode,
          value: `${bank.bankCode} ${bank.bankDescription}`,
          bankDescription: bank.bankDescription,
          bankAgreement: bank.bankAgreement ?? '',
        })),
    [banksList],
  );

  const selectedBankOption = useMemo(
    () => bankOptions.find((option) => option.key == bankObligationInfo.bankCode) ?? null,
    [bankOptions, bankObligationInfo.bankCode],
  );

  useEffect(() => {
    dispatch(getBanksList());
  }, [dispatch]);

  useEffect(() => {
    if (!bankObligationInfo.bankCode) {
      setBranchesList([]);
      return;
    }

    let cancelled = false;

    const loadBranches = async () => {
      const result = await dispatch(getBankBranchesList({bankCode: bankObligationInfo.bankCode}));
      if (!cancelled && getBankBranchesList.fulfilled.match(result)) {
        setBranchesList(getListFromResponse(result.payload));
      }
    };

    loadBranches();

    return () => {
      cancelled = true;
    };
  }, [bankObligationInfo.bankCode, dispatch]);

  const onBankChange = useCallback(
    (option) => {
      if (!option) {
        dispatch(
          setBankObligationInfo({
            bankCode: '',
            bankName: '',
            isAutomatedBank: false,
            branchCode: '',
            bankAccountNo: '',
          }),
        );
        return;
      }

      dispatch(
        setBankObligationInfo({
          bankCode: option.key,
          bankName: option.bankDescription,
          isAutomatedBank: option.bankAgreement !== '',
          branchCode: '',
          bankAccountNo: '',
        }),
      );
    },
    [dispatch],
  );

  const onBranchSelect = useCallback(
    (branch) => {
      dispatch(
        setBankObligationInfo({
          branchCode: branch?.branchCode ?? '',
          bankAccountNo: '',
        }),
      );
    },
    [dispatch],
  );

  const onBranchClear = useCallback(() => {
    dispatch(
      setBankObligationInfo({
        branchCode: '',
        bankAccountNo: '',
      }),
    );
  }, [dispatch]);

  const onAccountChange = useCallback(
    (value) => {
      dispatch(setBankObligationInfo({bankAccountNo: value}));
    },
    [dispatch],
  );

  const actions = {
    goToStep: (step) => dispatch(handleCurrentStep(step)),
    setShowPopup: (popup) => dispatch(setShowPopup(popup)),
    validatePaymentMethod: (data) => dispatch(paymentMethodValidation(data)),
  };

  return {
    selectedBanData,
    bankObligationInfo,
    bankOptions,
    branchesList,
    selectedBankOption,
    onBankChange,
    onBranchSelect,
    onBranchClear,
    onAccountChange,
    actions,
  };
};
